import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Save, Plus, Edit3, Trash2, Loader2 } from "lucide-react";
import { PageContainer, PageHeader, Card, Pill, SectionTitle } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/layout/DataTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const RATE_EMPTY = { code: "", name: "", type: "VAT", rate: 0, status: "Active" };
const GROUP_EMPTY = { code: "", name: "", tax_rate_ids: [], status: "Active" };

export default function TaxSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;

  const [settings, setSettings] = useState(null);
  const [branchCurrency, setBranchCurrency] = useState("");
  const [rateDialog, setRateDialog] = useState(false);
  const [groupDialog, setGroupDialog] = useState(false);
  const [editingRateId, setEditingRateId] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [rateForm, setRateForm] = useState(RATE_EMPTY);
  const [groupForm, setGroupForm] = useState(GROUP_EMPTY);
  const [taxProfile, setTaxProfile] = useState(null);
  const [ftaFrom, setFtaFrom] = useState("");
  const [ftaTo, setFtaTo] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["tax-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/tax-settings", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const { data: complianceData } = useQuery({
    queryKey: ["tax-compliance-profile", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/tax-compliance/profile", {
        params: { branch_id: branchId },
      });
      return res;
    },
    enabled: Boolean(branchId),
  });

  useEffect(() => {
    if (data) {
      setSettings(data.settings);
      setBranchCurrency(data.branch_currency || "AED");
    }
  }, [data]);

  useEffect(() => {
    if (complianceData?.profile) {
      setTaxProfile(complianceData.profile);
    }
  }, [complianceData]);

  const countryPresets = complianceData?.country_presets ?? [];

  const saveSettingsMutation = useMutation({
    mutationFn: () =>
      api.put(
        "/tax-settings",
        { settings, branch_currency: branchCurrency },
        { params: { branch_id: branchId } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tax-settings", branchId] });
      toast.success("Tax settings saved");
    },
    onError: () => toast.error("Failed to save tax settings"),
  });

  const saveRateMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...rateForm,
        rate: Number(rateForm.rate),
      };
      if (editingRateId) {
        return api.put(`/tax-rates/${editingRateId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/tax-rates", payload, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tax-settings", branchId] });
      setRateDialog(false);
      toast.success("Tax rate saved");
    },
    onError: () => toast.error("Failed to save tax rate"),
  });

  const saveGroupMutation = useMutation({
    mutationFn: async () => {
      if (editingGroupId) {
        return api.put(`/tax-groups/${editingGroupId}`, groupForm, { params: { branch_id: branchId } });
      }
      return api.post("/tax-groups", groupForm, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tax-settings", branchId] });
      setGroupDialog(false);
      toast.success("Tax group saved");
    },
    onError: () => toast.error("Failed to save tax group"),
  });

  const deleteRateMutation = useMutation({
    mutationFn: (id) => api.delete(`/tax-rates/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tax-settings", branchId] });
      toast.success("Tax rate deleted");
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id) => api.delete(`/tax-groups/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tax-settings", branchId] });
      toast.success("Tax group deleted");
    },
  });

  const saveProfileMutation = useMutation({
    mutationFn: () =>
      api.put("/tax-compliance/profile", taxProfile, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      setTaxProfile(res.data.profile);
      queryClient.invalidateQueries({ queryKey: ["tax-compliance-profile", branchId] });
      toast.success("GCC tax profile saved");
    },
    onError: () => toast.error("Failed to save tax profile"),
  });

  const downloadFtaExport = async () => {
    if (!ftaFrom || !ftaTo) {
      toast.error("Select date range for FTA export");
      return;
    }
    try {
      const { data: blob } = await api.get("/tax-compliance/fta-export", {
        params: { branch_id: branchId, date_from: ftaFrom, date_to: ftaTo },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `fta-vat-export-${ftaFrom}-${ftaTo}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("FTA export downloaded");
    } catch {
      toast.error("FTA export failed");
    }
  };

  function applyCountryPreset(code) {
    const preset = countryPresets.find((c) => c.code === code);
    if (!preset || !taxProfile) return;
    setTaxProfile((p) => ({
      ...p,
      country_code: code,
      default_vat_rate: preset.default_vat_rate,
      e_invoicing_standard: preset.e_invoicing_standard,
    }));
  }

  if (isLoading || !settings) {
    return (
      <PageContainer>
        <div className="h-40 grid place-items-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageContainer>
    );
  }

  const currencies = data?.currencies || [];
  const taxRates = data?.tax_rates || [];
  const taxGroups = data?.tax_groups || [];

  return (
    <PageContainer>
      <PageHeader
        title="Tax Settings"
        subtitle={`VAT & currency — ${user?.default_branch?.name || "branch"}`}
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#16A34A] hover:bg-emerald-700"
            data-testid="tax-save"
            onClick={() => saveSettingsMutation.mutate()}
            disabled={saveSettingsMutation.isPending}
          >
            {saveSettingsMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
            Save Changes
          </Button>
        }
      />

      <Card className="p-5 mb-4">
        <SectionTitle>VAT & Currency Configuration</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-5 gap-y-3">
          <Field label="VAT Registration Number (TRN)">
            <Input
              value={settings.tax_number || ""}
              onChange={(e) => setSettings((s) => ({ ...s, tax_number: e.target.value }))}
              className="h-9 font-mono"
            />
          </Field>
          <Field label="Default VAT Rate (%)">
            <Input
              type="number"
              value={settings.default_vat_rate}
              onChange={(e) => setSettings((s) => ({ ...s, default_vat_rate: Number(e.target.value) }))}
              className="h-9 text-right"
            />
          </Field>
          <Field label="Branch Currency">
            <Select value={branchCurrency} onValueChange={setBranchCurrency}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="POS Display Currency">
            <Select
              value={settings.display_currency || branchCurrency}
              onValueChange={(v) => setSettings((s) => ({ ...s, display_currency: v }))}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2">
            <Field label="VAT Inclusive Pricing">
              <div className="flex items-center gap-2 h-9 px-3 border rounded-md">
                <Switch
                  checked={settings.vat_inclusive}
                  onCheckedChange={(v) => setSettings((s) => ({ ...s, vat_inclusive: v }))}
                />
                <span className="text-[12.5px] text-slate-700">Show prices VAT-inclusive in POS</span>
              </div>
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Multi-Currency">
              <div className="flex items-center gap-2 h-9 px-3 border rounded-md">
                <Switch
                  checked={settings.allow_multi_currency}
                  onCheckedChange={(v) => setSettings((s) => ({ ...s, allow_multi_currency: v }))}
                />
                <span className="text-[12.5px] text-slate-700">
                  Enable multi-currency (company: {data?.company_currency || "AED"})
                </span>
              </div>
            </Field>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <SectionTitle className="!mb-0">Tax Rates</SectionTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              data-testid="tax-rate-new"
              onClick={() => {
                setEditingRateId(null);
                setRateForm({ ...RATE_EMPTY, code: `TAX-${taxRates.length + 1}` });
                setRateDialog(true);
              }}
            >
              <Plus size={13} className="mr-1.5" /> New Rate
            </Button>
          </div>
          <DataTable
            testIdPrefix="tax-rates"
            columns={[
              { key: "name", label: "Name", bold: true },
              { key: "type", label: "Type" },
              { key: "rate", label: "Rate (%)", align: "right", render: (v) => <span className="font-mono">{v}%</span> },
              { key: "status", label: "Status", render: (v) => <Pill tone={v === "Active" ? "green" : "default"}>{v}</Pill> },
            ]}
            rows={taxRates}
            rowActions={(row) => [
              {
                title: "Edit",
                icon: <Edit3 size={14} />,
                onClick: () => {
                  setEditingRateId(row.id);
                  setRateForm({ code: row.code, name: row.name, type: row.type, rate: row.rate, status: row.status });
                  setRateDialog(true);
                },
              },
              {
                title: "Delete",
                icon: <Trash2 size={14} />,
                danger: true,
                onClick: () => {
                  if (window.confirm(`Delete rate ${row.name}?`)) deleteRateMutation.mutate(row.id);
                },
              },
            ]}
          />
        </Card>

        <Card className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <SectionTitle className="!mb-0">Tax Groups</SectionTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              data-testid="tax-group-new"
              onClick={() => {
                setEditingGroupId(null);
                setGroupForm({ ...GROUP_EMPTY, code: `grp-${taxGroups.length + 1}` });
                setGroupDialog(true);
              }}
            >
              <Plus size={13} className="mr-1.5" /> New Group
            </Button>
          </div>
          <DataTable
            testIdPrefix="tax-groups"
            columns={[
              { key: "name", label: "Group", bold: true },
              { key: "components", label: "Components" },
              { key: "items", label: "Items", align: "right" },
              { key: "status", label: "Status", render: (v) => <Pill tone="green">{v}</Pill> },
            ]}
            rows={taxGroups}
            rowActions={(row) => [
              {
                title: "Edit",
                icon: <Edit3 size={14} />,
                onClick: () => {
                  setEditingGroupId(row.id);
                  setGroupForm({
                    code: row.code,
                    name: row.name,
                    tax_rate_ids: row.tax_rate_ids || [],
                    status: row.status,
                  });
                  setGroupDialog(true);
                },
              },
              {
                title: "Delete",
                icon: <Trash2 size={14} />,
                danger: true,
                onClick: () => {
                  if (window.confirm(`Delete group ${row.name}?`)) deleteGroupMutation.mutate(row.id);
                },
              },
            ]}
          />
        </Card>
      </div>

      {taxProfile && (
        <Card className="p-5 mt-4">
          <SectionTitle>GCC Tax Profile & E-Invoicing</SectionTitle>
          <p className="text-sm text-muted-foreground mb-4">
            Country-specific VAT, TRN on receipts, ZATCA QR/XML (KSA), and UAE FTA export.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-5 gap-y-3">
            <Field label="Country">
              <Select
                value={taxProfile.country_code}
                onValueChange={(code) => applyCountryPreset(code)}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {countryPresets.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} — {c.name} ({c.default_vat_rate}% VAT)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="E-invoicing standard">
              <Select
                value={taxProfile.e_invoicing_standard}
                onValueChange={(v) => setTaxProfile((p) => ({ ...p, e_invoicing_standard: v }))}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="uae_fta">UAE FTA</SelectItem>
                  <SelectItem value="zatca_phase2">KSA ZATCA Phase 2</SelectItem>
                  <SelectItem value="gcc_standard">GCC standard</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Legal name (on invoice)">
              <Input
                value={taxProfile.legal_name || ""}
                onChange={(e) => setTaxProfile((p) => ({ ...p, legal_name: e.target.value }))}
                className="h-9"
              />
            </Field>
            <Field label="Profile VAT rate (%)">
              <Input
                type="number"
                value={taxProfile.default_vat_rate}
                onChange={(e) =>
                  setTaxProfile((p) => ({ ...p, default_vat_rate: Number(e.target.value) }))
                }
                className="h-9 text-right"
              />
            </Field>
            <Field label="Invoice footer line 1">
              <Input
                value={taxProfile.invoice_footer_line1 || ""}
                onChange={(e) => setTaxProfile((p) => ({ ...p, invoice_footer_line1: e.target.value }))}
                className="h-9"
              />
            </Field>
            <Field label="Invoice footer line 2">
              <Input
                value={taxProfile.invoice_footer_line2 || ""}
                onChange={(e) => setTaxProfile((p) => ({ ...p, invoice_footer_line2: e.target.value }))}
                className="h-9"
              />
            </Field>
            {taxProfile.e_invoicing_standard === "zatca_phase2" && (
              <>
                <Field label="ZATCA environment">
                  <Select
                    value={taxProfile.zatca_environment || "sandbox"}
                    onValueChange={(v) => setTaxProfile((p) => ({ ...p, zatca_environment: v }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">Sandbox</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="ZATCA device ID">
                  <Input
                    value={taxProfile.zatca_device_id || ""}
                    onChange={(e) => setTaxProfile((p) => ({ ...p, zatca_device_id: e.target.value }))}
                    className="h-9 font-mono"
                  />
                </Field>
              </>
            )}
            <div className="md:col-span-2">
              <Field label="Show TRN on receipt">
                <div className="flex flex-wrap items-center gap-4 h-9 px-3 border rounded-md">
                  <label className="flex items-center gap-2 text-[12.5px]">
                    <Switch
                      checked={taxProfile.show_trn_on_receipt}
                      onCheckedChange={(v) =>
                        setTaxProfile((p) => ({ ...p, show_trn_on_receipt: v }))
                      }
                    />
                    Print TRN
                  </label>
                  <label className="flex items-center gap-2 text-[12.5px]">
                    <Switch
                      checked={taxProfile.show_trn_b2b_only}
                      onCheckedChange={(v) =>
                        setTaxProfile((p) => ({ ...p, show_trn_b2b_only: v }))
                      }
                    />
                    B2B only (customer must have TRN)
                  </label>
                </div>
              </Field>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              size="sm"
              className="bg-[#16A34A] hover:bg-emerald-700"
              onClick={() => saveProfileMutation.mutate()}
              disabled={saveProfileMutation.isPending}
            >
              Save GCC profile
            </Button>
          </div>
          <div className="mt-6 pt-4 border-t grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <Field label="FTA export from">
              <Input type="date" value={ftaFrom} onChange={(e) => setFtaFrom(e.target.value)} className="h-9" />
            </Field>
            <Field label="FTA export to">
              <Input type="date" value={ftaTo} onChange={(e) => setFtaTo(e.target.value)} className="h-9" />
            </Field>
            <div className="md:col-span-2">
              <Button variant="outline" size="sm" className="h-9" onClick={downloadFtaExport}>
                <Download size={14} className="mr-1.5" />
                Download UAE FTA VAT CSV
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Dialog open={rateDialog} onOpenChange={setRateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingRateId ? "Edit Tax Rate" : "New Tax Rate"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <Field label="Code"><Input value={rateForm.code} onChange={(e) => setRateForm((f) => ({ ...f, code: e.target.value }))} className="h-9 font-mono" disabled={Boolean(editingRateId)} /></Field>
            <Field label="Name"><Input value={rateForm.name} onChange={(e) => setRateForm((f) => ({ ...f, name: e.target.value }))} className="h-9" /></Field>
            <Field label="Type">
              <Select value={rateForm.type} onValueChange={(v) => setRateForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="VAT">VAT</SelectItem>
                  <SelectItem value="Service">Service</SelectItem>
                  <SelectItem value="Exempt">Exempt</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Rate (%)"><Input type="number" value={rateForm.rate} onChange={(e) => setRateForm((f) => ({ ...f, rate: e.target.value }))} className="h-9 text-right" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialog(false)}>Cancel</Button>
            <Button className="bg-[#16A34A] hover:bg-emerald-700" onClick={() => saveRateMutation.mutate()} disabled={saveRateMutation.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingGroupId ? "Edit Tax Group" : "New Tax Group"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <Field label="Code"><Input value={groupForm.code} onChange={(e) => setGroupForm((f) => ({ ...f, code: e.target.value }))} className="h-9 font-mono" disabled={Boolean(editingGroupId)} /></Field>
            <Field label="Name"><Input value={groupForm.name} onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))} className="h-9" /></Field>
            <Field label="Linked Tax Rates">
              <div className="border rounded-md p-2 space-y-1 max-h-32 overflow-y-auto">
                {taxRates.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-[12.5px]">
                    <input
                      type="checkbox"
                      checked={(groupForm.tax_rate_ids || []).includes(r.id)}
                      onChange={(e) => {
                        setGroupForm((f) => ({
                          ...f,
                          tax_rate_ids: e.target.checked
                            ? [...(f.tax_rate_ids || []), r.id]
                            : (f.tax_rate_ids || []).filter((id) => id !== r.id),
                        }));
                      }}
                    />
                    {r.name} ({r.rate}%)
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialog(false)}>Cancel</Button>
            <Button className="bg-[#16A34A] hover:bg-emerald-700" onClick={() => saveGroupMutation.mutate()} disabled={saveGroupMutation.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
